/**
 * Default procedure templates database.
 * Static ophthalmology consent procedure data — used to seed new clinics.
 */

export const DEFAULT_PROCEDURES = {
  blefaroplastia: {
    id: 'blefaroplastia',
    name: 'Blefaroplastia',
    description: 'A blefaroplastia consiste em um procedimento cirúrgico que tem por finalidade a remoção do excesso de pele e, em alguns casos, de parte do músculo palpebral e da gordura orbital.',
    details: [
      'A incisão (corte) na pálpebra superior é feita, em geral, na prega palpebral a fim de esconder uma improvável cicatriz.',
      'A cirurgia de blefaroplastia pode ser realizada nas pálpebras superiores e inferiores, com algumas variações, conforme as particularidades e expectativas individuais do(a) paciente.',
      'Os benefícios finais da cirurgia das pálpebras aparecerão dentro de algumas semanas do procedimento, mas podem levar até 1 (um) ano para as linhas da incisão refinarem totalmente.',
      'A Blefaroplastia poderá ser realizada sob sedação ou anestesia geral, sendo necessário o (a) paciente passar por consulta para avaliação.'
    ],
    risks: [
      'Cicatrizes desfavoráveis',
      'Visão embaçada ou temporariamente prejudicada',
      'Olhos secos',
      'Dificuldade de fechar seus olhos',
      'Sangramento (hematoma)',
      'Cicatriz inestética',
      'Quemose conjuntival (edema da conjuntiva)',
      'Lagoftalmo (não fechamento completo das pálpebras)',
      'Lesões Córneas ou do Globo Ocular',
      'Retração Palpebral',
      'Diplopia',
      'Infecção',
      'Acúmulo de líquido (seroma)',
      'Dormência e outras alterações de sensibilidade na pele',
      'Riscos anestésicos',
      'Disfunção na pálpebra envolvendo posição anormal das pálpebras superiores e/ou inferiores',
      'Dor, que pode perdurar',
      'Descoloração da pele e inchaço',
      'Fios de suturas podem espontaneamente emergir da pele',
      'Trombose venosa profunda, complicações cardíacas e pulmonares',
      'Possibilidade de cirurgia revisional',
      'Perda de visão',
      'Recorrência do entrópio'
    ],
    preOp: [
      'Comunicar até 2 dias antes da cirurgia ocorrências como gripe, indisposição, febre, período menstrual etc.',
      'Evite tomar aspirina ou remédios contendo AAS (ácido acetil salicílico) e vitamina E, pelo menos nas duas semanas que antecedem à cirurgia',
      'Fazer 8 (oito) horas de jejum total para sólidos e 6 (seis) horas para líquidos, inclusive água',
      'Retirar o esmalte das unhas',
      'Caso seja usuário de lentes de contato, suspender a sua utilização'
    ],
    postOp: [
      'Mantenha repouso relativo nos 8 primeiros dias, e evite atividade física nos 30 primeiros dias',
      'Realizar compressas frias no local ao menos 4 (quatro) vezes por dia nos primeiros 3 (três) dias',
      'Não coçar os olhos até cicatrizar',
      'Não dormir de lado nem de bruços por 10 (dez) dias',
      'Evitar movimentos que possam traumatizar os olhos, como carregar peso, lavar roupa, sauna, corrida, academia ou qualquer esporte de impacto por 30 dias',
      'Evitar piscinas e praias por 30 (trinta) dias',
      'Em qualquer circunstância, evite esfregar ou coçar os olhos'
    ],
    recovery: '1 (um) ano para as linhas da incisão refinarem totalmente'
  },
  catarata: {
    id: 'catarata',
    name: 'Facoemulsificação de Catarata',
    description: 'A facoemulsificação é uma técnica cirúrgica para remoção da catarata, na qual o cristalino opacificado é fragmentado por ultrassom e aspirado, sendo substituído por uma lente intraocular artificial.',
    details: [
      'O procedimento é realizado através de uma pequena incisão de aproximadamente 2-3mm na córnea.',
      'A lente intraocular (LIO) é implantada após a remoção da catarata, podendo corrigir também problemas refrativos como miopia, hipermetropia ou astigmatismo.',
      'A cirurgia geralmente é realizada sob anestesia tópica (colírio) com sedação leve.',
      'A recuperação visual inicial ocorre nas primeiras 24-48 horas, com estabilização completa em 4-6 semanas.'
    ],
    risks: [
      'Infecção intraocular (endoftalmite)',
      'Edema ou descolamento de retina',
      'Aumento da pressão intraocular',
      'Ruptura da cápsula posterior',
      'Luxação ou deslocamento da lente intraocular',
      'Astigmatismo residual',
      'Visão embaçada temporária',
      'Inflamação intraocular',
      'Sangramento intraocular',
      'Opacificação da cápsula posterior (catarata secundária)',
      'Edema macular cistóide',
      'Diplopia',
      'Necessidade de cirurgia adicional',
      'Perda parcial ou total da visão',
      'Reação alérgica aos medicamentos utilizados',
      'Glaucoma secundário'
    ],
    preOp: [
      'Realizar exames oftalmológicos completos incluindo biometria e cálculo de lente intraocular',
      'Suspender uso de lentes de contato conforme orientação (geralmente 7 dias antes)',
      'Fazer jejum de 8 horas para sólidos e 6 horas para líquidos',
      'Não utilizar maquiagem nos olhos no dia da cirurgia',
      'Comparecer acompanhado, pois não poderá dirigir após o procedimento'
    ],
    postOp: [
      'Utilizar o protetor ocular durante o sono por 7 dias',
      'Aplicar os colírios prescritos rigorosamente nos horários indicados',
      'Evitar coçar ou pressionar os olhos',
      'Não abaixar a cabeça abruptamente nos primeiros 7 dias',
      'Evitar esforço físico intenso por 15 dias',
      'Não entrar em piscinas, praias ou saunas por 30 dias',
      'Usar óculos de sol para proteção contra luz intensa',
      'Comparecer às consultas de retorno conforme agendado'
    ],
    recovery: '4-6 semanas para estabilização completa da visão'
  },
  pterigium: {
    id: 'pterigium',
    name: 'Exérese de Pterígio',
    description: 'A cirurgia de pterígio consiste na remoção do tecido fibrovascular que cresce sobre a córnea, geralmente associada a um auto-enxerto conjuntival para prevenir recorrência.',
    details: [
      'O procedimento remove o tecido anormal que invade a córnea e pode comprometer a visão.',
      'Utiliza-se técnica com auto-enxerto conjuntival retirado da própria conjuntiva do paciente, fixado com cola biológica ou suturas.',
      'A cirurgia é realizada sob anestesia local com sedação leve.',
      'O pterígio tem tendência a recorrência, especialmente em pacientes com exposição solar intensa.'
    ],
    risks: [
      'Recorrência do pterígio (taxa de 5-15%)',
      'Infecção',
      'Sangramento subconjuntival',
      'Cicatriz na córnea',
      'Astigmatismo induzido',
      'Diplopia (visão dupla)',
      'Deslocamento do enxerto',
      'Granuloma conjuntival',
      'Olho seco',
      'Irritação ocular persistente',
      'Alteração da superfície ocular',
      'Reação à cola biológica ou suturas',
      'Perfuração escleral (raro)',
      'Visão embaçada temporária'
    ],
    preOp: [
      'Suspender uso de lentes de contato 3 dias antes da cirurgia',
      'Evitar maquiagem nos olhos no dia do procedimento',
      'Jejum de 6 horas para cirurgias com sedação',
      'Informar sobre uso de anticoagulantes',
      'Comparecer acompanhado'
    ],
    postOp: [
      'Usar colírios antibióticos e anti-inflamatórios conforme prescrição',
      'Evitar exposição solar direta por 30 dias',
      'Usar óculos de sol com proteção UV sempre que sair',
      'Não coçar ou esfregar os olhos',
      'Evitar atividades físicas intensas por 15 dias',
      'Não frequentar piscinas, praias ou saunas por 30 dias',
      'Aplicar compressas frias se houver desconforto',
      'Comparecer às consultas de acompanhamento regularmente'
    ],
    recovery: '2-4 semanas para cicatrização inicial, 3 meses para estabilização completa'
  },
  sondagem_vias_lacrimais: {
    id: 'sondagem_vias_lacrimais',
    name: 'Sondagem de Vias Lacrimais',
    description: 'A sondagem das vias lacrimais é o procedimento de escolha para pacientes com obstrução baixa do sistema lacrimal, especialmente em casos congênitos. Trata-se de uma intervenção que visa desobstruir o canal e restabelecer o fluxo adequado da lágrima para a cavidade nasal.',
    details: [
      'O procedimento consiste em sondagem da via lacrimal através do sistema nasolacrimal com o objetivo de desobstruir o canal.',
      'A obstrução baixa das vias lacrimais descreve uma condição na qual observa-se a ausência de drenagem da lágrima por obstrução após o saco lacrimal.',
      'Os pacientes apresentam lacrimejamento importante com presença ou não de secreção, podendo haver dilatação do saco lacrimal.',
      'A anestesia utilizada pode ser local com sedação ou anestesia geral, variando conforme a idade do paciente e avaliação da equipe anestésica.',
      'A taxa média de sucesso deste procedimento é de aproximadamente 90%.'
    ],
    risks: [
      'Edema local',
      'Hematomas',
      'Lacrimejamento persistente',
      'Sangramento nasal',
      'Insucesso do procedimento',
      'Necessidade de reintervenção',
      'Indicação de dacriocistorrinostomia em caso de falha',
      'Infecção',
      'Perfuração da via lacrimal',
      'Riscos anestésicos'
    ],
    preOp: [
      'Realizar exames pré-operatórios conforme solicitação médica',
      'Comunicar ocorrências como gripe, indisposição ou febre',
      'Fazer jejum conforme orientação (8 horas para sólidos, 6 horas para líquidos)',
      'Comparecer acompanhado'
    ],
    postOp: [
      'Evitar traumas locais',
      'Usar medicações conforme prescrição',
      'Manter cuidados de higiene para prevenção de infecção',
      'Posição adequada para dormir conforme orientação',
      'Comparecer às consultas de revisão',
      'Repouso conforme orientação médica',
      'Comunicar imediatamente quaisquer anormalidades'
    ],
    recovery: '1-2 semanas para recuperação inicial'
  },
  resurfacing_laser: {
    id: 'resurfacing_laser',
    name: 'Resurfacing da Pele a Laser CO2',
    description: 'O resurfacing da pele a laser é um procedimento que utiliza feixes de laser para remover camadas superficiais da pele, promovendo a regeneração de uma pele mais saudável e jovem. É utilizado para tratar rugas finas, manchas solares, cicatrizes e outras imperfeições da pele ao redor dos olhos.',
    details: [
      'O procedimento pode suavizar rugas e linhas finas, especialmente "pés de galinha" ao redor dos olhos.',
      'Promove regeneração de pele mais suave, uniforme e com textura melhorada.',
      'É eficaz na remoção de manchas solares, pigmentação irregular e alterações na coloração da pele.',
      'Existem lasers ablativos (removem camadas superficiais) e não ablativos (estimulam colágeno sem remover camadas).',
      'O médico controla a profundidade da penetração do laser conforme necessário.',
      'Dispositivos de refrigeração são utilizados para proteger a pele e minimizar desconforto.',
      'Pode ser realizado com anestesia local, sedação leve ou anestesia geral.'
    ],
    risks: [
      'Vermelhidão prolongada',
      'Inchaço',
      'Infecção',
      'Alterações na pigmentação da pele (hiperpigmentação ou hipopigmentação)',
      'Cicatrizes',
      'Reativação de herpes labial',
      'Ectrópio (eversão da pálpebra)',
      'Queimaduras',
      'Sensibilidade aumentada ao sol',
      'Riscos anestésicos',
      'Resultado insatisfatório'
    ],
    preOp: [
      'Avaliação detalhada da condição da pele',
      'Comunicar uso de medicamentos, especialmente isotretinoína',
      'Evitar exposição solar excessiva nas semanas anteriores',
      'Jejum conforme tipo de anestesia',
      'Suspender uso de produtos com ácidos na pele'
    ],
    postOp: [
      'Usar cremes especiais conforme prescrição',
      'Evitar exposição ao sol rigorosamente',
      'Usar protetor solar de alto fator',
      'Manter a pele hidratada',
      'Não coçar ou esfregar a região tratada',
      'Comparecer às consultas de acompanhamento',
      'Aguardar semanas/meses para resultado final'
    ],
    recovery: '1-2 semanas para cicatrização inicial, 3-6 meses para resultado final'
  },
  estrabismo: {
    id: 'estrabismo',
    name: 'Correção de Estrabismo',
    description: 'A cirurgia de correção do estrabismo relaxa ou tensiona os músculos extraoculares, alterando o alinhamento de um olho em relação ao outro. O procedimento visa corrigir o desvio ocular e pode melhorar tanto a função visual quanto a aparência estética.',
    details: [
      'Durante o procedimento, as pálpebras são mantidas abertas por um espéculo.',
      'O médico faz uma abertura na conjuntiva para acessar os músculos oculares.',
      'Não são feitos cortes na pele e o globo ocular NÃO é removido durante a cirurgia.',
      'A cirurgia pode ser realizada por retrocesso (enfraquece função muscular) ou ressecção (fortalece função muscular).',
      'No retrocesso, altera-se o local de fixação do músculo no globo ocular.',
      'Na ressecção, encurta-se o músculo para que fique menos elástico e ganhe força.',
      'Pode ser necessária modificação ou extensão do procedimento durante a cirurgia.'
    ],
    risks: [
      'Diplopia (visão dupla) temporária ou permanente',
      'Hipocorreção (desvio residual)',
      'Hipercorreção (inversão do desvio)',
      'Infecção',
      'Sangramento',
      'Perfuração escleral',
      'Perda ou deslizamento de sutura muscular',
      'Cicatrização anormal',
      'Necessidade de reoperação',
      'Alteração da posição palpebral',
      'Granuloma de sutura',
      'Riscos anestésicos'
    ],
    preOp: [
      'Avaliação oftalmológica completa com medição do desvio',
      'Exames pré-operatórios conforme solicitação',
      'Jejum de 8 horas para sólidos e 6 horas para líquidos',
      'Comunicar uso de medicamentos',
      'Comparecer acompanhado'
    ],
    postOp: [
      'Usar colírios conforme prescrição',
      'Evitar coçar ou esfregar os olhos',
      'Evitar atividades físicas intensas por 2 semanas',
      'Evitar piscinas e praias por 30 dias',
      'Usar óculos de sol para proteção',
      'Comparecer às consultas de acompanhamento',
      'Comunicar diplopia, dor intensa ou secreção'
    ],
    recovery: '2-4 semanas para recuperação inicial, ajuste fino pode levar meses'
  },
  toxina_botulinica_estrabismo: {
    id: 'toxina_botulinica_estrabismo',
    name: 'Toxina Botulínica para Estrabismo',
    description: 'A aplicação de toxina botulínica enfraquece o músculo tratado, impedindo sua contração e permitindo que o músculo antagonista aja sem restrição, favorecendo o alinhamento ocular. É uma alternativa menos invasiva à cirurgia de estrabismo.',
    details: [
      'A aplicação é feita por via injetável com agulha fina diretamente no músculo a ser tratado.',
      'Pode ser realizada sob sedação no centro cirúrgico ou no consultório sem anestesia.',
      'O efeito é temporário, durando de 4 a 6 meses, variando de pessoa para pessoa.',
      'A taxa de melhora do estrabismo com toxina botulínica varia de 50-60% dos casos.',
      'Não melhora a visão, apenas corrige o desvio.',
      'Após a segunda aplicação sem correção, pode ser indicada cirurgia.'
    ],
    risks: [
      'Ptose (queda da pálpebra)',
      'Inversão do desvio',
      'Desvio vertical (olho para cima ou para baixo)',
      'Olho vermelho',
      'Infecção local',
      'Diplopia temporária',
      'Necessidade de reaplicação',
      'Necessidade de cirurgia posterior',
      'Reação alérgica'
    ],
    preOp: [
      'Avaliação oftalmológica completa',
      'Medição precisa do desvio',
      'Jejum se procedimento com sedação',
      'Comunicar alergias conhecidas',
      'Comparecer acompanhado se sedação'
    ],
    postOp: [
      'Usar medicações conforme prescrição',
      'Manter higiene local',
      'Comparecer aos retornos médicos',
      'Não esfregar os olhos',
      'Comunicar ptose ou alterações visuais'
    ],
    recovery: '1-2 semanas para efeito inicial, efeito completo em 4-6 semanas'
  },
  dacriocistorrinostomia: {
    id: 'dacriocistorrinostomia',
    name: 'Dacriocistorrinostomia (DCR)',
    description: 'A dacriocistorrinostomia é o procedimento cirúrgico de escolha nos pacientes com obstrução baixa das vias lacrimais. Consiste na confecção de uma via alternativa para a drenagem da lágrima, comunicando o saco lacrimal diretamente com a cavidade nasal.',
    details: [
      'Indicada para obstrução baixa das vias lacrimais com ou sem dacriocistite crônica.',
      'Pacientes apresentam lacrimejamento importante, podendo haver secreção constante e episódios de infecção.',
      'Pode ser necessária colocação de tubo de silicone por 2-3 meses.',
      'A dacriectomia (retirada do saco lacrimal) pode ser realizada quando impossível refazer a comunicação.',
      'A anestesia pode ser local com sedação ou geral.',
      'Taxa de sucesso por volta de 90%.'
    ],
    risks: [
      'Hematomas',
      'Edema',
      'Lacrimejamento persistente',
      'Sangramento pela incisão ou nariz',
      'Deiscência de sutura',
      'Hipertrofia cicatricial',
      'Cicatriz inestética',
      'Olho seco',
      'Quemose conjuntival',
      'Lagoftalmo',
      'Necessidade de reoperação',
      'Hematoma retrobulbar',
      'Lesões corneanas ou do globo ocular',
      'Ptose palpebral',
      'Infecções',
      'Fístula cefalorraquidiana (rara)'
    ],
    preOp: [
      'Exames pré-operatórios incluindo dacriocistografia se indicado',
      'Jejum de 8 horas para sólidos',
      'Comunicar uso de anticoagulantes',
      'Tratar infecções ativas antes da cirurgia',
      'Comparecer acompanhado'
    ],
    postOp: [
      'Evitar assoar o nariz com força por 2 semanas',
      'Usar medicações conforme prescrição',
      'Compressas frias para edema',
      'Manter cabeça elevada ao dormir',
      'Evitar esforço físico intenso',
      'Comparecer para retirada do tubo de silicone (se utilizado)',
      'Comunicar sangramento abundante ou febre'
    ],
    recovery: '1-2 semanas para hematomas, 2-3 meses para resultado final'
  },
  calazio_tumor_palpebral: {
    id: 'calazio_tumor_palpebral',
    name: 'Exérese de Calázio/Tumor Palpebral',
    description: 'A exérese de tumor palpebral consiste na remoção cirúrgica de lesão na pálpebra, incluindo calázio (cisto da glândula de Meibômio) e outros tumores benignos ou malignos, com reconstrução da região acometida quando necessário.',
    details: [
      'O procedimento remove o tumor que pode acometer apenas a pele ou toda a espessura da pálpebra.',
      'Tumores podem causar sintomas como vermelhidão, sensação de cisco, ardência, dor e ptose.',
      'Pode ser necessária biópsia por congelação durante a cirurgia.',
      'A reconstrução pode ser simples (sutura) ou complexa (retalhos ou enxertos).',
      'Enxertos podem ser do próprio paciente ou de banco de tecidos regulamentado.',
      'A anestesia pode ser local, local com sedação, ou geral.'
    ],
    risks: [
      'Hematomas',
      'Edema (pode durar até 6 meses a 1 ano)',
      'Deiscência de sutura',
      'Hipertrofia cicatricial',
      'Cicatriz inestética',
      'Olho seco',
      'Quemose conjuntival',
      'Lagoftalmo',
      'Necrose do enxerto ou retalho',
      'Necessidade de reoperações',
      'Hematoma retrobulbar',
      'Lesões corneanas',
      'Ptose palpebral',
      'Retração palpebral',
      'Ectrópio ou entrópio',
      'Assimetria',
      'Diplopia',
      'Infecção',
      'Recorrência da lesão'
    ],
    preOp: [
      'Avaliação da lesão e indicação cirúrgica',
      'Exames pré-operatórios',
      'Jejum conforme tipo de anestesia',
      'Comunicar uso de anticoagulantes',
      'Fotos pré-operatórias para documentação'
    ],
    postOp: [
      'Compressas frias nas primeiras 48 horas',
      'Usar medicações conforme prescrição',
      'Evitar esforço físico',
      'Proteger a região de traumas',
      'Comparecer para retirada de pontos',
      'Aguardar resultado de biópsia se realizada',
      'Comunicar sangramento, dor intensa ou alterações visuais'
    ],
    recovery: '1-2 semanas para cicatrização inicial, meses para resultado estético final'
  }
};
